import { Box, Typography } from '@mui/material';
import { ActionButton } from './ActionButton';
import { ConfirmationCard } from './ConfirmationCard';
import { ToolCallCard } from './ToolCallCard';
import type { ChatMessage, AssistantAction } from './types';

interface ChatMessageItemProps {
  message: ChatMessage;
  onAction?: (action: AssistantAction) => void;
  onConfirm?: (toolCallId: string) => Promise<void> | void;
  onReject?: (toolCallId: string) => Promise<void> | void;
  actionsEnabled?: boolean;
}

export const ChatMessageItem = ({
  message,
  onAction,
  onConfirm,
  onReject,
  actionsEnabled = true,
}: ChatMessageItemProps) => {
  switch (message.role) {
    case 'user':
      return <UserMessage content={message.content ?? ''} />;
    case 'assistant':
      return (
        <AssistantMessage
          content={message.content ?? ''}
          actions={message.metadata?.actions}
          onAction={onAction}
          actionsEnabled={actionsEnabled}
        />
      );
    case 'tool_call':
    case 'tool_result':
      return <ToolCallCard message={message} />;
    case 'confirmation':
      return <ConfirmationCard message={message} onConfirm={onConfirm} onReject={onReject} />;
    case 'system':
      return message.content ? (
        <Box sx={{ textAlign: 'center', my: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {message.content}
          </Typography>
        </Box>
      ) : null;
    default:
      return null;
  }
};

const UserMessage = ({ content }: { content: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 0.5 }}>
    <Box
      sx={{
        maxWidth: '85%',
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>
        {content}
      </Typography>
    </Box>
  </Box>
);

const AssistantMessage = ({
  content,
  actions,
  onAction,
  actionsEnabled,
}: {
  content: string;
  actions?: AssistantAction[];
  onAction?: (action: AssistantAction) => void;
  actionsEnabled: boolean;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', my: 0.5 }}>
    <Box
      sx={{
        maxWidth: '85%',
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        backgroundColor: 'action.hover',
        color: 'text.primary',
      }}
    >
      <AssistantMarkdown text={content} />
    </Box>
    {actions && actions.length > 0 && (
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
        {actions.map((action, i) => (
          <ActionButton
            key={`${action.type}:${action.label}:${i}`}
            action={action}
            onAction={onAction}
            disabled={!actionsEnabled}
          />
        ))}
      </Box>
    )}
  </Box>
);

/**
 * Simple markdown-like rendering for assistant text.
 * Handles: **bold**, *italic*, `code`, and line breaks.
 * Full markdown rendering can be added via a library in Phase 1.
 */
const AssistantMarkdown = ({ text }: { text: string }) => {
  if (!text) return null;

  const lines = text.split('\n');

  return (
    <>
      {lines.map((line, i) => (
        <Typography
          key={i}
          variant="body2"
          component="div"
          sx={{
            fontSize: 13,
            wordBreak: 'break-word',
            ...(line.trimStart().startsWith('- ') || line.trimStart().startsWith('* ')
              ? { pl: 1.5, '&::before': { content: '"•"', position: 'absolute', ml: -1.5 }, position: 'relative' }
              : {}),
            ...(line.trimStart().match(/^\d+\.\s/)
              ? { pl: 2 }
              : {}),
            ...(line.trimStart().startsWith('```')
              ? { fontFamily: 'monospace', fontSize: 12, backgroundColor: 'action.selected', px: 0.5, borderRadius: 0.5 }
              : {}),
            mb: line === '' ? 0.5 : 0,
          }}
        >
          <InlineFormatted text={line} />
        </Typography>
      ))}
    </>
  );
};

/** Handle inline formatting: **bold**, *italic*, `code` */
const InlineFormatted = ({ text }: { text: string }) => {
  const parts: Array<{ text: string; bold?: boolean; italic?: boolean; code?: boolean }> = [];
  let remaining = text;

  while (remaining.length > 0) {
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      parts.push({ text: codeMatch[1], code: true });
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      parts.push({ text: boldMatch[1], bold: true });
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      parts.push({ text: italicMatch[1], italic: true });
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    const nextSpecial = remaining.search(/[`*]/);
    if (nextSpecial === -1) {
      parts.push({ text: remaining });
      break;
    }
    if (nextSpecial > 0) {
      parts.push({ text: remaining.slice(0, nextSpecial) });
      remaining = remaining.slice(nextSpecial);
    } else {
      parts.push({ text: remaining[0] });
      remaining = remaining.slice(1);
    }
  }

  return (
    <>
      {parts.map((part, i) => {
        if (part.code) {
          return (
            <Typography
              key={i}
              component="code"
              sx={{
                fontFamily: 'monospace',
                fontSize: 12,
                backgroundColor: 'action.selected',
                px: 0.5,
                borderRadius: 0.5,
              }}
            >
              {part.text}
            </Typography>
          );
        }
        if (part.bold) {
          return <strong key={i}>{part.text}</strong>;
        }
        if (part.italic) {
          return <em key={i}>{part.text}</em>;
        }
        return <span key={i}>{part.text}</span>;
      })}
    </>
  );
};
